
import { OperationRecord, SystemConfig, AuditLog, Cliente, Comercial, ReferencePrice, QuoteData, BillingGoal, BillingRecord, CommercialGoal, Payment, StagedInvoice, StagedUpsTrackingEvent, UpsCostReconciliationRow, UpsCollectionAlertRow, CuentaRecaudo, ComprobantePagoRecaudo, ComprobanteCliente, SecurityAlert, SecurityAlertEstado, MailboxListResponse, MailboxMessageDetail, MailboxUnreadCounts, Recoleccion, RecoleccionesPage, ResumenRecolecciones, RecoleccionDetalle, LimpiezaAnulacion, ResultadoRecoleccionAuto, NotaCreditoItem, NotasCreditoResponse, DailyInboxResponse, DailyInboxProposal, CarrierLookupResult } from '../types.ts';

import { getCsrfToken } from '../src/lib/csrf';
import { toBillingRecordSaveBody } from '../src/lib/billingFields';
import { normalizeClientBody, normalizeSalesRepBody } from '../src/lib/entityFields';
import { normalizePaymentBody } from '../src/lib/paymentFields';
import { normalizeQuoteBody } from '../src/lib/quoteFields';
import { toRecordSaveBody } from '../src/lib/operationFields';
import type { CarrierTrackingDetail } from '../src/lib/carrierTrackingDetail';
import { downloadWithProgress, postFormWithProgress } from '../src/lib/measuredTransfer';
import { runMeasuredJob } from '../src/lib/jobProgress';
import i18n from '../src/i18n';

const API_BASE = '/api';

/** Operator debt broken down by nature (see calcularDeudaOperador). */
export interface OperatorDebt {
  totalDebtCOP: number;
  pendingShipments: number;
  facturado: { montoCOP: number; guias: number; facturas: number };
  sinFacturar: { montoCOP: number; guias: number };
}

export const EMPTY_OPERATOR_DEBT: OperatorDebt = {
  totalDebtCOP: 0, pendingShipments: 0,
  facturado: { montoCOP: 0, guias: 0, facturas: 0 }, sinFacturar: { montoCOP: 0, guias: 0 },
};

export interface GuiaListaParaFacturar {
  id: string;
  guia: string | null;
  cliente: string | null;
  operador: string | null;
  fecha: string | null;
  totalAFacturar: number;
  costoFlete: number;
  facturaOperador: string | null;
  /** Cannot invoice without tax ID: Siigo issues a legal document in the name of an identified party. */
  nitCliente?: string;
  puedeFacturar?: boolean;
}

/** Carrier invoiced, Logis did not: revenue owed, whether or not it is ready to invoice yet. */
export interface GuiaSinFacturaLogis extends GuiaListaParaFacturar {
  lista: boolean;
  /** What is holding it back, in business language, empty when it is simply ready. */
  faltantes: string[];
}

export interface QuoteDeskCounts {
  drafts: number;
  waitingClient: number;
  readyToRegister: number;
}

export interface PortalDeskCounts {
  saldoPendiente: number;
  quotesToApprove: number;
  shipmentsInTransit: number;
}

export interface AccountingDeskCounts {
  pendingReceipts: number;
  readyToInvoice: number;
  readyCommissions: number;
}

export interface OperatorCasesStats {
  total: number;
  abiertos: number;
  vencidos: number;
  estancados: number;
  needsFollowUp: number;
}

export interface PendientesFacturar {
  listas: GuiaListaParaFacturar[];
  totalListas: number;
  montoListoCOP: number;
  /** Of the ready list, how many lack the client's tax ID and therefore cannot be invoiced yet. */
  sinNit?: number;
  /** Why remaining records cannot be invoiced yet — the question that follows the number. */
  bloqueadas: { sinPago: number; sinFacturaOperador: number; total: number };
  sinFacturaLogis?: GuiaSinFacturaLogis[];
  totalSinFacturaLogis?: number;
  montoSinFacturarCOP?: number;
}

export interface OperatorInvoice {
  invoiceNumber: string;
  guias: number;
  montoCOP: number;
  pagada: boolean;
  fechaPago: string | null;
  fechaMasAntigua: string | null;
}

export interface NotificationGroup {
  entidad: string;
  entidad_id: string | null;
  tipo: string;
  titulo: string;
  mensaje: string;
  link: string | null;
  fecha_creacion: string;
  prioridad: 'critico' | 'alerta' | 'exito' | 'info';
  archivada: boolean;
  latestId: string;
  count: number;
  unreadCount: number;
  ids: string[];
}

export interface TourProgressItem {
  roleKey: string;
  stepIndex: number;
  status: 'in_progress' | 'completed' | 'dismissed';
  fechaActualizacion: string;
}

// Security hardening (27/07): the session now travels in an httpOnly cookie (the browser
// sends it on every same-origin request) -- there is no longer a token in localStorage to
// read or an Authorization header to build. X-CSRF-Token is harmless on GET (the backend
// only requires it on mutations) so it is always sent, without distinguishing the method here.
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken()
  };
};

/**
 * Error of a payment confirmation (T-14e). When the amount does not cover the quote total the
 * server answers `requiresPartialReason`, so the screen can ask Accounting for the reason.
 */
export class PaymentError extends Error {
  constructor(message: string, readonly requiresPartialReason = false, readonly missing = 0) {
    super(message);
  }
}
const paymentError = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({}));
  return new PaymentError(data.error || fallback, !!data.requiresPartialReason, Number(data.missing) || 0);
};

const generateFallbackUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** Shared between getRecordsPaginated and getRecordsFiltered (28/07) -- single source for
 * mapping /api/records filters to query params, so the two do not diverge over time. */
const buildRecordsFilterParams = (params: {
  includeAnuladas?: boolean; search?: string; fechaDesde?: string; fechaHasta?: string;
  vendedor?: string; operador?: string; country?: string; estado?: string; pago?: string; tieneGuia?: string;
  estadoCobro?: string; tipoOperacion?: string; operationType?: string;
  /** design spec §4 "No silos": 'internal' | 'partner' | 'agency', optionally with originId. */
  origin?: string; originId?: string;
}): URLSearchParams => {
  const qs = new URLSearchParams();
  if (params.includeAnuladas) qs.set('include_anuladas', 'true');
  if (params.search) qs.set('search', params.search);
  if (params.fechaDesde) qs.set('dateDesde', params.fechaDesde);
  if (params.fechaHasta) qs.set('dateHasta', params.fechaHasta);
  if (params.vendedor && params.vendedor !== 'TODOS') qs.set('vendedor', params.vendedor);
  if (params.operador && params.operador !== 'TODOS') qs.set('operator', params.operador);
  if (params.country) qs.set('country', params.country);
  if (params.estado && params.estado !== 'TODOS') qs.set('estado', params.estado);
  if (params.pago && params.pago !== 'TODOS') qs.set('pago', params.pago);
  if (params.tieneGuia && params.tieneGuia !== 'TODOS') qs.set('tieneGuia', params.tieneGuia);
  if (params.estadoCobro && params.estadoCobro !== 'TODOS') qs.set('estadoCobro', params.estadoCobro);
  const opType = params.operationType || params.tipoOperacion;
  if (opType && opType !== 'TODOS') qs.set('operationType', opType);
  if (params.origin && params.origin !== 'TODOS') qs.set('origin', params.origin);
  if (params.originId) qs.set('originId', params.originId);
  return qs;
};

/**
 * Column order for server-paginated tables (27/08). Sorting these in the browser would only
 * reorder the page on screen, so the key travels to the endpoint and the database orders the
 * whole set before slicing. The server validates the key against its own whitelist.
 */
export interface ServerSortParams {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

const appendSortParams = (qs: URLSearchParams, params: ServerSortParams): URLSearchParams => {
  if (params.sortBy) {
    qs.set('sortBy', params.sortBy);
    qs.set('sortDir', params.sortDir === 'desc' ? 'desc' : 'asc');
  }
  return qs;
};

const getSafeUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return generateFallbackUUID();
};

export const dataService = {
  async init(): Promise<void> {
    let retries = 5;
    while (retries > 0) {
      try {
        const response = await fetch(`${API_BASE}/records`, {
          headers: getAuthHeaders()
        });
        if (response.status === 401 || response.status === 403) return; // Not logged in or invalid token
        if (!response.ok) throw new Error("No se pudo conectar al servidor de base de datos.");
        return; // Success
      } catch (error) {
        retries--;
        if (retries === 0) {
          console.error("Error al inicializar el servicio de datos:", error);
          throw error;
        }
        console.log(`Error de conexión, reintentando... (${retries} intentos restantes)`);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
      }
    }
  },

  async getRecords(includeAnuladas = false, onlyOwnComercial = false): Promise<OperationRecord[]> {
    const params = new URLSearchParams();
    if (includeAnuladas) params.set('include_anuladas', 'true');
    if (onlyOwnComercial) params.set('onlyOwnComercial', 'true');
    const qs = params.toString();
    const response = await fetch(`${API_BASE}/records${qs ? `?${qs}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener registros.");
    return await response.json();
  },

  /** Single record, no list filters -- see GET /api/records/:id, used for the deep-link
   * "edit this operation" (Operations.tsx) which must not depend on which filter/page is
   * active in the table. */
  async getRecordById(id: string): Promise<OperationRecord | null> {
    const response = await fetch(`${API_BASE}/records/${id}`, {
      headers: getAuthHeaders()
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Error al obtener la operación.");
    return await response.json();
  },

  /** "Ver detalles del envío" of the portal tracking (T-8): carrier sheet + full history. */
  async getCarrierTrackingDetail(id: string): Promise<CarrierTrackingDetail> {
    const response = await fetch(`${API_BASE}/records/${encodeURIComponent(id)}/carrier-tracking`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al consultar el detalle del envío.');
    return data;
  },

  /** Real pagination (28/07) -- same pattern as getAuditLogsPaginated/getClientsPaginated. */
  async getRecordsPaginated(params: {
    page: number; pageSize: number; includeAnuladas?: boolean;
    search?: string; fechaDesde?: string; fechaHasta?: string; vendedor?: string; operador?: string;
    country?: string; estado?: string; pago?: string; tieneGuia?: string; estadoCobro?: string; tipoOperacion?: string;
    origin?: string; originId?: string;
  } & ServerSortParams): Promise<{
    items: OperationRecord[]; total: number; page: number; pageSize: number; totalPages: number;
    aggregates: Record<string, number>; availableYears: string[];
  }> {
    const qs = buildRecordsFilterParams(params);
    qs.set('page', String(params.page));
    qs.set('pageSize', String(params.pageSize));
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/records?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener registros.");
    return await response.json();
  },

  /**
   * Same filters as getRecordsPaginated but WITHOUT page/pageSize -- the backend responds with
   * the full flat array (legacy path, see GET /api/records). For "Export to Excel": must export
   * EVERYTHING matching the active filters, not only the page visible on screen.
   */
  async getRecordsFiltered(params: {
    includeAnuladas?: boolean; search?: string; fechaDesde?: string; fechaHasta?: string;
    vendedor?: string; operador?: string; country?: string; estado?: string; pago?: string; tieneGuia?: string;
    estadoCobro?: string; tipoOperacion?: string; origin?: string; originId?: string;
  }): Promise<OperationRecord[]> {
    const qs = buildRecordsFilterParams(params);
    const response = await fetch(`${API_BASE}/records${qs.toString() ? `?${qs.toString()}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener registros.");
    return await response.json();
  },

  /** Snapshot of shipments pending invoice, broken down by sales rep (06/08). */
  async getPendingInvoicesByComercial(): Promise<{ total: number; byComercial: { comercial: string; pendientes: number }[] }> {
    const response = await fetch(`${API_BASE}/records/pending-invoices-by-comercial`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener facturas pendientes.");
    return await response.json();
  },

  /**
   * Returns the Automatic pickup result (11/08): when saving an operation with a shipment,
   * the server tries to schedule it and reports whether it succeeded or why not. Never fails
   * the save -- that is why it rides in the response and not as an error.
   */
  async saveRecord(record: Partial<OperationRecord>): Promise<{ recoleccion?: ResultadoRecoleccionAuto }> {
    const body = toRecordSaveBody({
      ...record,
      id: getSafeUUID(),
      fechaCreacion: new Date().toISOString(),
    });
    const response = await fetch(`${API_BASE}/records`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al guardar el registro.");
    }
    const data = await response.json().catch(() => ({}));
    return { recoleccion: data?.recoleccion };
  },

  /** Returns `limpiazaAnulacion` only when this edit was the one that voided the shipment/tracking guide --
   * the summary of everything released in cascade (see limpiarDerivadosDeGuiaAnulada in server.ts).
   * Other edits return `undefined` and callers ignore it. */
  async updateRecord(updatedRecord: OperationRecord): Promise<{ limpiezaAnulacion?: LimpiezaAnulacion; recoleccion?: ResultadoRecoleccionAuto }> {
    const response = await fetch(`${API_BASE}/records/${updatedRecord.id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toRecordSaveBody(updatedRecord)),
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      const err: any = new Error(errorData.error || "Error al actualizar el registro.");
      if (errorData.motivo) err.motivo = errorData.motivo;
      if (errorData.pendingBalance !== undefined) err.pendingBalance = errorData.pendingBalance;
      throw err;
    }
    try {
      return await response.json();
    } catch {
      return {};
    }
  },

  async getPendingAvalRecords(): Promise<OperationRecord[]> {
    const response = await fetch(`${API_BASE}/records/pending-aval`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las operaciones pendientes de aval.");
    return await response.json();
  },

  async resolveRecordAval(id: string, decision: 'aprobado' | 'rechazado', motivo?: string): Promise<void> {
    const response = await fetch(`${API_BASE}/records/${id}/aval`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ decision, motivo })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Error al resolver el aval.");
    }
  },

  async markAsDelivered(id: string, opts?: { motivo?: string; portal?: boolean }): Promise<void> {
    const path = opts?.portal
      ? `${API_BASE}/portal/records/${id}/delivery`
      : `${API_BASE}/records/${id}/delivery`;
    const response = await fetch(path, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(opts?.motivo ? { motivo: opts.motivo } : {}),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al marcar como entregado.");
    }
  },

  async getQuotesReadyForOps(): Promise<{ id: string; consecutivo: string; cliente: string; paymentSource: string; fechaAprobacion?: string | null }[]> {
    const response = await fetch(`${API_BASE}/quotes/ready-for-ops`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error("Error al obtener cotizaciones listas para operaciones.");
    return await response.json();
  },

  async changeRecordAddress(id: string, ciudadOrigen: string, codigoPostalOrigen: string, motivo: string): Promise<void> {
    const response = await fetch(`${API_BASE}/records/${id}/address-change`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ciudadOrigen, codigoPostalOrigen, motivo })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al cambiar la dirección.");
    }
  },

  // Guided "Correct Operation" flow (05/08): voids the current UPS shipment/tracking guide and creates a new
  // one with the corrected data -- UPS does not support modifying an already-created shipment, only void+recreate.
  async correctUpsShipment(id: string, data: {
    origen?: string;
    ciudadOrigen?: string;
    codigoPostalOrigen?: string;
    direccionOrigen?: string;
    proveedorNombre?: string;
    proveedorContacto?: string;
    proveedorTelefono?: string;
    paisDestino?: string;
    ciudadDestino: string;
    codigo_postal: string;
    direccionDestino: string;
    cliente?: string;
    serviceCode?: string;
    serviceName?: string;
    pesoKg: number;
    largo: number;
    ancho: number;
    alto: number;
    dimensiones?: any[];
    motivo: string;
  }): Promise<{ guiaAnterior: string; guiaNueva: string; labelUrl?: string }> {
    const response = await fetch(`${API_BASE}/records/${id}/correct-ups-shipment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || "Error al corregir la operación.");
    }
    return result;
  },

  /**
   * Deletes an operation. `tambienEnSiigo` propagates the delete to the Siigo invoice.
   *
   * Returns what happened on the other side instead of swallowing it: if it deleted here
   * and not there, the two systems just diverged and the person who did it must know.
   */
  async deleteRecord(id: string, tambienEnSiigo = false): Promise<{ siigo?: { intentado: boolean; ok: boolean; motivo?: string; reason?: string } }> {
    const response = await fetch(`${API_BASE}/records/${id}${tambienEnSiigo ? '?tambienEnSiigo=1' : ''}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar el registro.");
    return response.json().catch(() => ({}));
  },

  // --- Compatibility Methods ---
  // `saveVendors`/`saveOperators` (and their POST endpoints in server.ts) were removed on 14/08:
  // they were a placeholder that never persisted anything (`res.json({success:true})` without
  // touching the DB) and were only called by `SettingsModal.tsx`, an orphan component no app
  // button ever opened. The real sales-rep list is managed in `GestionComerciales.tsx`
  // (`comerciales` table); logistics operators, in Settings (UPS/FedEx credentials).
  // `getVendors`/`getOperators` are kept: they are real queries (`DISTINCT` on `records`) and
  // `Operations.tsx` uses them for form suggestions.
  async getVendors(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/config/vendors`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return [];
    return await response.json();
  },

  async getOperators(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/config/operators`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return [];
    return await response.json();
  },

  // --- System Config ---
  async getSystemConfig(): Promise<SystemConfig | null> {
    const response = await fetch(`${API_BASE}/system-config`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return null;
    return await response.json();
  },

  async updateSystemConfig(config: SystemConfig): Promise<void> {
    const response = await fetch(`${API_BASE}/system-config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error("Error al actualizar configuración.");
  },

  // --- Audit Logs ---
  async getAuditLogs(): Promise<AuditLog[]> {
    const response = await fetch(`${API_BASE}/audit-logs`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener logs de auditoría.");
    return await response.json();
  },

  /** Direct fetch by id (05/08), see GET /api/audit-logs/:id -- does not depend on the
   * page/filter currently loaded in the list, same pattern as getRecordById. */
  async getAuditLogById(id: string): Promise<AuditLog | null> {
    const response = await fetch(`${API_BASE}/audit-logs/${id}`, {
      headers: getAuthHeaders()
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Error al obtener el registro de auditoría.");
    return await response.json();
  },

  /** Real pagination (23/07) -- audit_logs is append-only and only grows, see server.ts.
   * usuarioId/fechaDesde/fechaHasta (30/07): new filters, unified filter aesthetics --
   * availableUsers is computed by the backend over the WHOLE tenant, not only the current page. */
  async getAuditLogsPaginated(params: { page: number; pageSize: number; search?: string; action?: string; usuarioId?: string; fechaDesde?: string; fechaHasta?: string } & ServerSortParams): Promise<{ items: AuditLog[]; total: number; page: number; pageSize: number; totalPages: number; availableUsers: { usuario_id: string; usuario_nombre: string }[] }> {
    const qs = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      search: params.search || '',
      action: params.action || 'all',
    });
    if (params.usuarioId) qs.set('usuarioId', params.usuarioId);
    if (params.fechaDesde) qs.set('dateDesde', params.fechaDesde);
    if (params.fechaHasta) qs.set('dateHasta', params.fechaHasta);
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/audit-logs?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener logs de auditoría.");
    return await response.json();
  },

  async getSecurityAlerts(params?: { estado?: SecurityAlertEstado | 'all'; tipo?: string }): Promise<{ items: SecurityAlert[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.estado) qs.set('estado', params.estado);
    if (params?.tipo) qs.set('tipo', params.tipo);
    const response = await fetch(`${API_BASE}/security/anomalies?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las alertas de seguridad.");
    return await response.json();
  },

  async reviewSecurityAlert(id: string, estado: 'revisada' | 'descartada'): Promise<void> {
    const response = await fetch(`${API_BASE}/security/anomalies/${id}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify({ estado })
    });
    if (!response.ok) throw new Error("Error al actualizar la alerta de seguridad.");
  },

  async blockIpManually(data: { ip: string; reason: string; durationHours?: number }): Promise<void> {
    const response = await fetch(`${API_BASE}/security/blocked-ips`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Error al bloquear la IP.");
    }
  },

  async getTRM(): Promise<number | null> {
    const response = await fetch(`${API_BASE}/config/trm`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.rate;
  },

  // --- Clients ---
  async getClients(): Promise<Cliente[]> {
    const response = await fetch(`${API_BASE}/clients`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener clientes.");
    return await response.json();
  },

  /** Direct fetch by id (05/08), see GET /api/clients/:id -- does not depend on the
   * page/filter currently loaded in the list, same pattern as getRecordById. */
  async getClientById(id: string): Promise<Cliente | null> {
    const response = await fetch(`${API_BASE}/clients/${id}`, {
      headers: getAuthHeaders()
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Error al obtener el cliente.");
    return await response.json();
  },

  /** Real pagination (28/07) -- same pattern as getAuditLogsPaginated, see server.ts. */
  async getClientsPaginated(params: { page: number; pageSize: number; search?: string; tipoCliente?: string } & ServerSortParams): Promise<{ items: Cliente[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const qs = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      search: params.search || '',
      tipoCliente: params.tipoCliente || 'TODOS',
    });
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/clients?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener clientes.");
    return await response.json();
  },

  async saveClient(client: Partial<Cliente>): Promise<void> {
    const isUpdate = !!client.id;
    const url = isUpdate ? `${API_BASE}/clients/${client.id}` : `${API_BASE}/clients`;
    const method = isUpdate ? 'PUT' : 'POST';
    const body = normalizeClientBody(client as Record<string, unknown>);

    const response = await fetch(url, {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...body,
        id: client.id || getSafeUUID()
      })
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Error al ${isUpdate ? 'actualizar' : 'guardar'} cliente.`);
    }
  },

  /** Deletes a client. `tambienEnSiigo` propagates the delete to their Siigo record. */
  async deleteClient(id: string, tambienEnSiigo = false): Promise<{ siigo?: { intentado: boolean; ok: boolean; motivo?: string; reason?: string } }> {
    const response = await fetch(`${API_BASE}/clients/${id}${tambienEnSiigo ? '?tambienEnSiigo=1' : ''}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Error al eliminar el cliente.');
    }
    return response.json().catch(() => ({}));
  },

  // --- Sales reps ---
  async getSalesReps(): Promise<Comercial[]> {
    const response = await fetch(`${API_BASE}/sales-reps`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener comerciales.");
    return await response.json();
  },

  async saveSalesRep(salesRep: Partial<Comercial>): Promise<void> {
    const isUpdate = !!salesRep.id;
    const url = isUpdate ? `${API_BASE}/sales-reps/${salesRep.id}` : `${API_BASE}/sales-reps`;
    const method = isUpdate ? 'PUT' : 'POST';
    const body = normalizeSalesRepBody(salesRep as Record<string, unknown>);

    const response = await fetch(url, {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...body,
        id: salesRep.id || getSafeUUID()
      })
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Error al ${isUpdate ? 'actualizar' : 'guardar'} comercial.`);
    }
  },

  async deleteSalesRep(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/sales-reps/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error || "Error al eliminar comercial.");
    }
  },

  // --- Reference Prices ---
  async getReferencePrices(): Promise<ReferencePrice[]> {
    const response = await fetch(`${API_BASE}/reference-prices`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener precios de referencia.");
    return await response.json();
  },

  async saveReferencePrice(price: Partial<ReferencePrice>): Promise<void> {
    const isUpdate = !!price.id;
    const url = isUpdate ? `${API_BASE}/reference-prices/${price.id}` : `${API_BASE}/reference-prices`;
    const method = isUpdate ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...price,
        id: price.id || getSafeUUID()
      })
    });
    if (!response.ok) throw new Error(`Error al ${isUpdate ? 'actualizar' : 'guardar'} precio de referencia.`);
  },

  async deleteReferencePrice(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/reference-prices/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar precio de referencia.");
  },

  async seedReferencePrices(): Promise<void> {
    const response = await fetch(`${API_BASE}/reference-prices/seed`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Error al sembrar precios de referencia.");
    }
  },

  // --- Quotes ---
  async getQuotes(): Promise<QuoteData[]> {
    const response = await fetch(`${API_BASE}/quotes`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener cotizaciones.");
    return await response.json();
  },

  /** Direct fetch by id (05/08), see GET /api/quotes/:id -- does not depend on the
   * page/filter currently loaded in history, same pattern as getRecordById. */
  async getQuoteById(id: string): Promise<QuoteData | null> {
    const response = await fetch(`${API_BASE}/quotes/${id}`, {
      headers: getAuthHeaders()
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Error al obtener la cotización.");
    return await response.json();
  },

  /** Edit history of a quote, scoped to the owner (creator or assigned sales rep)
   * or to roles that see all quotations (06/08, §33.21). */
  async getQuoteHistory(id: string): Promise<{ accion: string; timestamp: string; usuario: string }[]> {
    const response = await fetch(`${API_BASE}/quotes/${id}/history`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener el historial de la cotización.");
    return await response.json();
  },

  /** Real pagination (28/07) -- same pattern as getAuditLogsPaginated/getClientsPaginated. */
  async getQuotesPaginated(params: { page: number; pageSize: number; search?: string; operador?: string; registrada?: string; cliente?: string; fechaDesde?: string; fechaHasta?: string } & ServerSortParams): Promise<{ items: QuoteData[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const qs = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) });
    if (params.search) qs.set('search', params.search);
    if (params.operador && params.operador !== 'TODOS') qs.set('operator', params.operador);
    if (params.registrada && params.registrada !== 'TODAS') qs.set('registrada', params.registrada);
    if (params.cliente && params.cliente !== 'TODOS') qs.set('client', params.cliente);
    if (params.fechaDesde) qs.set('dateDesde', params.fechaDesde);
    if (params.fechaHasta) qs.set('dateHasta', params.fechaHasta);
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/quotes?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener cotizaciones.");
    return await response.json();
  },

  async getPendingPaymentQuotes(): Promise<{ id: string; consecutivo: string; cliente: string; fechaAprobacion: string; horasTranscurridas: number }[]> {
    const response = await fetch(`${API_BASE}/quotes/pending-payment`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return [];
    return await response.json();
  },

  async getOperatorDebt(): Promise<OperatorDebt> {
    const response = await fetch(`${API_BASE}/kpi/operator-debt`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return EMPTY_OPERATOR_DEBT;
    return await response.json();
  },

  async getPendientesFacturar(): Promise<PendientesFacturar> {
    const response = await fetch(`${API_BASE}/kpi/pending-to-invoice`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return { listas: [], totalListas: 0, montoListoCOP: 0, bloqueadas: { sinPago: 0, sinFacturaOperador: 0, total: 0 } };
    return await response.json();
  },

  async getAccountingDesk(): Promise<AccountingDeskCounts> {
    const response = await fetch(`${API_BASE}/accounting/desk`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al cargar el escritorio de contabilidad.");
    return await response.json();
  },

  async getQuoteDesk(): Promise<QuoteDeskCounts> {
    const response = await fetch(`${API_BASE}/quotes/desk`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al cargar el escritorio de cotizaciones.");
    return await response.json();
  },

  async getPortalDesk(): Promise<PortalDeskCounts> {
    const response = await fetch(`${API_BASE}/portal/desk`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al cargar el escritorio del portal.");
    return await response.json();
  },

  async getOperatorCasesStats(): Promise<OperatorCasesStats> {
    const response = await fetch(`${API_BASE}/operator-cases?page=1&pageSize=1`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al cargar el resumen de casos.");
    const data = await response.json();
    const stats = data.stats || {};
    return {
      total: Number(stats.total) || 0,
      abiertos: Number(stats.abiertos) || 0,
      vencidos: Number(stats.vencidos) || 0,
      estancados: Number(stats.estancados) || 0,
      needsFollowUp: Number(stats.needsFollowUp) || 0,
    };
  },

  async getNotasCredito(): Promise<NotasCreditoResponse> {
    const response = await fetch(`${API_BASE}/notas-credito`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return { items: [], pending: 0, pendingAmountCOP: 0, pendientes: 0, montoPendienteCOP: 0 };
    return await response.json();
  },

  async registrarNotaCredito(recordId: string, payload: { numero: string; montoCOP?: number }): Promise<{ success: boolean; numero: string; montoCOP: number; costoFlete: number }> {
    const response = await fetch(`${API_BASE}/records/${encodeURIComponent(recordId)}/nota-credito`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo registrar la nota crédito.');
    return data;
  },

  // Corrects the number/amount of an operator credit note ALREADY registered (16/08). Distinct from
  // the initial registration (POST above): this PATCH only applies to shipments with notaCreditoEstado
  // 'recibida' and the backend blocks it (409) if the shipment already has an issued Siigo invoice.
  async corregirNotaCredito(recordId: string, payload: { numero: string; montoCOP: number }): Promise<{ success: boolean; numero: string; montoCOP: number; costoFlete: number }> {
    const response = await fetch(`${API_BASE}/records/${encodeURIComponent(recordId)}/nota-credito`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo corregir la nota crédito.');
    return data;
  },

  async syncRecordToSiigo(recordId: string): Promise<{ siigo_invoice_number: string }> {
    const response = await fetch(`${API_BASE}/siigo/sync-record/${recordId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo facturar en Siigo.');
    return data;
  },

  async importarConciliacionSiigo(desde?: string, hasta?: string): Promise<{ success: boolean; facturasImportadas: number; guiasIndexadas: number; matchesGuiaExacta: number; matchesPendientesRevision: number }> {
    return runMeasuredJob({ kind: 'import', label: i18n.t('jobs.kinds.import'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ desde, hasta })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo importar el histórico de Siigo.');
      return data;
    });
  },

  // Re-matches what's already imported from Siigo against the operations that exist TODAY,
  // without calling Siigo (08/21, §10.3.7): automatic sync is incremental and never retries
  // matching invoices that were already imported before those operations existed.
  async recalcularConciliacionSiigoLocal(): Promise<{ success: boolean; facturasProcesadas: number; matchesGuiaExacta: number; matchesPendientesRevision: number; sinMatch: number }> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/recalculate-local`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo recalcular la conciliación de Siigo.');
      return data;
    });
  },

  /**
   * Reconciliation inbox, paginated on the server (19/08).
   *
   * Also returns per-status counts and the last-import summary: the figures the screen
   * needs to explain itself, which used to cost a separate network round-trip, or did
   * not exist at all.
   */
  async getConciliacionSiigo(params: { estado?: string; page?: number; pageSize?: number; q?: string } & ServerSortParams = {}): Promise<{
    results: any[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    conteos: { todos: number; sin_cruzar: number; pendiente_revision: number; confirmado: number; descartado: number };
    resumen: { facturasImportadas: number; ultimaImportacion: string | null };
  }> {
    const query = new URLSearchParams();
    if (params.estado) query.set('estado', params.estado);
    if (params.q) query.set('q', params.q);
    query.set('page', String(params.page || 1));
    query.set('pageSize', String(params.pageSize || 20));
    appendSortParams(query, params);
    const response = await fetch(`${API_BASE}/siigo/reconciliation?${query.toString()}`, {
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo cargar la conciliación de Siigo.');
    return data;
  },

  /**
   * Decide on a Siigo invoice. Addressed by the invoice id, not the reconciliation id:
   * most invoices have no reconciliation row (they match nothing) and those are exactly
   * the ones you need to be able to decide on.
   *
   * `vinculo` says which LOGISTRA operation it corresponds to, when a person decides.
   */
  async actualizarConciliacionSiigo(
    facturaId: string,
    estado: 'confirmado' | 'descartado' | 'pendiente_revision',
    notas?: string,
    vinculo?: { recordId?: string; freightOperationId?: string },
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/siigo/reconciliation/${encodeURIComponent(facturaId)}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ estado, notas, ...vinculo })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudo actualizar la conciliación.');
    }
  },

  /** LOGISTRA operations a invoice can be linked to, searched by shipment or client. */
  async buscarCandidatosConciliacion(q: string): Promise<{ results: Array<{ id: string; tipo: 'record' | 'freight_operation'; guia: string; cliente: string; total: number; fecha: string }> }> {
    const response = await fetch(`${API_BASE}/siigo/reconciliation/candidates?q=${encodeURIComponent(q)}`, {
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({ results: [] }));
    if (!response.ok) throw new Error(data.error || 'No se pudieron buscar operaciones.');
    return data;
  },

  /**
   * Sends a cash receipt to Siigo.
   *
   * Until now receipts lived only in LOGISTRA and had to be re-typed there. Sent with the
   * invoices they were applied against, which is what Siigo needs to lower the client balance.
   */
  async enviarReciboASiigo(paymentId: string): Promise<{ siigoVoucherId: string; siigoVoucherNumero: string | null }> {
    const response = await fetch(`${API_BASE}/payments/${encodeURIComponent(paymentId)}/siigo`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo enviar el recibo a Siigo.');
    return data;
  },

  /** Reverts in Siigo a receipt sent by mistake. The local row is kept. */
  async revertirReciboSiigo(paymentId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/payments/${encodeURIComponent(paymentId)}/siigo`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudo revertir el recibo en Siigo.');
    }
  },

  /** Sends a client record to Siigo: creates it if missing, updates it if it exists. */
  async sincronizarClienteConSiigo(clienteId: string): Promise<{ siigoCustomerId: string; creado: boolean }> {
    const response = await fetch(`${API_BASE}/siigo/clients/${encodeURIComponent(clienteId)}/sync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo sincronizar el cliente con Siigo.');
    return data;
  },

  /**
   * Deletes a Siigo invoice that has not yet reached DIAN.
   *
   * If it is already validated, the server responds 409 with `requiereNotaCredito`: not a
   * failure, the only possible response. That case is resolved with a credit note.
   */
  async eliminarFacturaSiigo(siigoInvoiceId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/siigo/invoices/${encodeURIComponent(siigoInvoiceId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudo eliminar la factura en Siigo.');
    }
  },

  /** Status of the automatic Siigo sync. */
  async getEstadoSincronizacionSiigo(): Promise<{
    automatica: boolean; intervaloMinutos: number; ultimaSincronizacion: string | null;
    ultimaImportacion: string | null; facturasCacheadas: number;
  }> {
    const response = await fetch(`${API_BASE}/siigo/sincronizacion`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo consultar el estado de la sincronización.');
    return data;
  },

  async importarComprasConciliacionSiigo(desde?: string, hasta?: string): Promise<{ success: boolean; facturasImportadas: number; topeAlcanzado: boolean; paginasProcesadas: number; advertencia?: string }> {
    return runMeasuredJob({ kind: 'import', label: i18n.t('jobs.kinds.import'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/import-purchases`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ desde, hasta })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo importar las facturas de compra de Siigo.');
      return data;
    });
  },

  // Real disbursements (supplier payments, `/v1/payment-receipts`) -- explicit owner research
  // (21/08): gives the real payment date that `/v1/purchases` never exposes (only its balance).
  async importarEgresosConciliacionSiigo(desde?: string, hasta?: string): Promise<{ success: boolean; egresosImportados: number; aplicacionesImportadas: number; topeAlcanzado: boolean; paginasProcesadas: number; advertencia?: string }> {
    return runMeasuredJob({ kind: 'import', label: i18n.t('jobs.kinds.import'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/import-expenses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ desde, hasta })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo importar los egresos de Siigo.');
      return data;
    });
  },

  // Cash receipts <-> shipments (21/08, explicit owner requirement: "everything that is not
  // matched, match it"). Additive; never deletes an existing match -- only adds missing ones.
  async recalcularRecibosConciliacionSiigo(desde?: string, hasta?: string): Promise<{ success: boolean; recibosRevisados: number; cruzadosNuevos: number; montoCruzadoNuevoCOP: number; topeAlcanzado: boolean; paginasProcesadas: number; advertencia?: string }> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/recalculate-receipts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ desde, hasta })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo recalcular el cruce de recibos de caja.');
      return data;
    });
  },

  async confirmarPagosClienteDesdeSiigo(): Promise<{ success: boolean; actualizados: number }> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/siigo/reconciliation/confirm-client-payments`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo confirmar el estado de pago desde Siigo.');
      return data;
    });
  },

  /** Read-only preview (16/08): summarizes what an import would do with what is already cached
   * localmente -- nunca escribe en records/freight_operations/clientes. */
  async getPreviewImportacionSiigo(): Promise<{
    modo: string; advertencia: string; operacionesDetectadas: number; guias: string[];
    clientesDetectados: { nit: string; yaExisteEnLogistra: boolean; facturas: number }[];
    ventasRelacionadas: number; costosRelacionadosConGuiaReal: number; comprasLigadasConConfianzaAlta: number;
    duplicadosPotenciales: { cantidad: number; guias: string[] };
    registrosAmbiguos: { cantidad: number; detalle: any[] };
  }> {
    const response = await fetch(`${API_BASE}/siigo/importacion-preview`, {
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'No se pudo generar la vista previa de importación.');
    return data;
  },

  async getOperatorInvoices(soloPendientes = false): Promise<{ items: OperatorInvoice[]; totalPendienteCOP: number }> {
    const response = await fetch(`${API_BASE}/operator-invoices${soloPendientes ? '?estado=pendiente' : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return { items: [], totalPendienteCOP: 0 };
    return await response.json();
  },

  async setOperatorInvoicePaid(invoiceNumber: string, pagada: boolean, fechaPago?: string): Promise<void> {
    const response = await fetch(`${API_BASE}/operator-invoices/${encodeURIComponent(invoiceNumber)}/pago`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ pagada, fechaPago })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudo registrar el pago al operador.');
    }
  },

  async saveQuote(quote: Partial<QuoteData>): Promise<{ success: boolean; consecutivo: string }> {
    const body = normalizeQuoteBody({
      ...quote,
      id: quote.id || getSafeUUID(),
      date: quote.date ?? quote.fecha ?? new Date().toISOString(),
      fecha: quote.fecha ?? quote.date ?? new Date().toISOString(),
    } as Record<string, unknown>);
    const response = await fetch(`${API_BASE}/quotes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error("Error al guardar cotización.");
    return await response.json();
  },

  async updateQuote(id: string, quote: Partial<QuoteData>): Promise<{ message: string; consecutivo: string }> {
    const body = normalizeQuoteBody(quote as Record<string, unknown>);
    const response = await fetch(`${API_BASE}/quotes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error("Error al actualizar cotización.");
    return await response.json();
  },

  async deleteQuote(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/quotes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar cotización.");
  },

  async invitePayment(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/quotes/${id}/invite-payment`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || "Error al invitar al cliente a pagar.");
  },

  async resendQuote(id: string): Promise<{ vecesCorregida: number }> {
    const response = await fetch(`${API_BASE}/quotes/${id}/resend`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || "Error al reenviar la cotización.");
    return await response.json();
  },

  // --- Billing Goals ---
  async getBillingGoals(): Promise<BillingGoal[]> {
    const response = await fetch(`${API_BASE}/billing-goals`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener metas de facturación.");
    return await response.json();
  },

  async saveBillingGoal(goal: Partial<BillingGoal>): Promise<void> {
    const response = await fetch(`${API_BASE}/billing-goals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...goal,
        id: goal.id || getSafeUUID()
      })
    });
    if (!response.ok) throw new Error("Error al guardar meta de facturación.");
  },

  async deleteBillingGoal(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/billing-goals/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar meta de facturación.");
  },

  // --- Billing Records ---
  async getBillingRecords(): Promise<BillingRecord[]> {
    const response = await fetch(`${API_BASE}/billing-records`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener registros de facturación.");
    return await response.json();
  },

  async saveBillingRecord(record: Partial<BillingRecord>): Promise<void> {
    const body = toBillingRecordSaveBody({
      ...record,
      id: record.id || getSafeUUID(),
    });
    const response = await fetch(`${API_BASE}/billing-records`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error("Error al guardar registro de facturación.");
  },

  async deleteBillingRecord(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/billing-records/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar registro de facturación.");
  },

  async getPayments(): Promise<Payment[]> {
    const response = await fetch(`${API_BASE}/payments`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener pagos.");
    return await response.json();
  },

  async createPayment(payment: { client_id: string; monto?: number; amount?: number; fecha?: string; date?: string; medio?: string; method?: string }): Promise<void> {
    const body = normalizePaymentBody(payment as Record<string, unknown>);
    const response = await fetch(`${API_BASE}/payments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        client_id: body.client_id,
        monto: body.monto,
        fecha: body.fecha,
        medio: body.medio,
      })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al registrar el pago.");
    }
  },

  async assignPayment(paymentId: string, recordId: string, montoAsignado: number): Promise<void> {
    const response = await fetch(`${API_BASE}/payments/${paymentId}/assign`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ record_id: recordId, monto_asignado: montoAsignado })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al asignar el pago.");
    }
  },

  async unassignPayment(paymentId: string, assignmentId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/payments/${paymentId}/assign/${assignmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al deshacer la asignación.");
    }
  },

  /** Edit a cash receipt (08/11). The backend rejects lowering the amount below what's already
   * matched, and changing the client on one with live matches -- both messages are actionable,
   * so they're propagated as-is instead of a generic text. */
  async updatePayment(id: string, cambios: { client_id?: string; monto?: number; amount?: number; fecha?: string; date?: string; medio?: string; method?: string }): Promise<void> {
    const body = normalizePaymentBody(cambios as Record<string, unknown>);
    const response = await fetch(`${API_BASE}/payments/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({
        client_id: body.client_id,
        monto: body.monto,
        fecha: body.fecha,
        medio: body.medio,
      })
    });
    if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo editar el recibo de caja.'));
  },

  /** Delete a cash receipt with its matches. Returns which shipments went back to
   * 'pendiente' so the user can be told in the same toast. */
  /** Deletes a cash receipt. `tambienEnSiigo` propagates the delete to the Siigo receipt. */
  async deletePayment(id: string, tambienEnSiigo = false): Promise<{ crucesEliminados: number; guiasAfectadas: string[]; siigo?: { intentado: boolean; ok: boolean; motivo?: string; reason?: string } }> {
    const response = await fetch(`${API_BASE}/payments/${id}${tambienEnSiigo ? '?tambienEnSiigo=1' : ''}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al eliminar el recibo de caja.');
    return data;
  },

  /** Client record for the current session (11/08). Returns null if this account is not a client
   * -- not an error; the portal simply hides the documents section. */
  async getMiCliente(): Promise<Cliente | null> {
    const response = await fetch(`${API_BASE}/my-client`, { headers: getAuthHeaders() });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo cargar su información de cliente.'));
    return await response.json();
  },

  /** Delete a weight-difference adjustment: also reverts the charge on the shipment/tracking guide. */
  async deleteAjustePeso(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/weight-adjustments/${id}`, {
      method: 'DELETE', headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo eliminar el ajuste de peso.'));
  },

  // --- Email ingestion of operator invoices (task #22) ---
  async pollInvoiceEmail(): Promise<{ skipped?: boolean; reason?: string; emailsSeen?: number; pdfsProcessed?: number; matched?: number; unmatched?: number; errors?: { context: string; error: string }[] }> {
    const response = await fetch(`${API_BASE}/invoices/poll-email`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al revisar el correo de facturas.");
    }
    return await response.json();
  },

  async getStagedInvoices(): Promise<StagedInvoice[]> {
    const response = await fetch(`${API_BASE}/invoices/staged`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las facturas en revisión.");
    return await response.json();
  },

  async rematchStagedInvoices(): Promise<{ revisadas: number; emparejadas: number; sinCambio: number }> {
    const response = await fetch(`${API_BASE}/invoices/staged/rematch`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al re-emparejar las facturas en revisión.");
    }
    return await response.json();
  },

  async applyStagedInvoice(stagedId: string, recordId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/invoices/staged/${stagedId}/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recordId })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al aplicar la factura al registro.");
    }
  },

  async discardStagedInvoice(stagedId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/invoices/staged/${stagedId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al descartar la factura.");
    }
  },

  // --- UPS real cost reconciliation (Reporte Diario) vs. costoFlete ---
  async pollUpsReconciliationEmail(): Promise<{ skipped?: boolean; reason?: string; emailsSeen?: number; sheetsProcessed?: number; rowsProcessed?: number; errors?: { context: string; error: string }[] }> {
    const response = await fetch(`${API_BASE}/ups-reconciliation/poll-email`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al revisar el correo de conciliación UPS.");
    }
    return await response.json();
  },

  async getDailyInbox(): Promise<DailyInboxResponse> {
    const response = await fetch(`${API_BASE}/operations/daily-inbox`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al cargar la bandeja Daily UPS.');
    return await response.json();
  },

  async createDailyInboxProposal(id: string, selected: string[]): Promise<{ success: boolean; recordId?: string; alreadyExisted?: boolean }> {
    const response = await fetch(`${API_BASE}/operations/daily-inbox/${id}/create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ selected }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al crear la operación desde el Daily.');
    return data;
  },

  async dismissDailyInboxProposal(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/operations/daily-inbox/${id}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al descartar la guía del Daily.');
  },

  async lookupCarrierTracking(guia: string): Promise<CarrierLookupResult> {
    const response = await fetch(`${API_BASE}/operations/carrier-lookup/${encodeURIComponent(guia)}`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al consultar la guía en UPS.');
    return data;
  },

  async queueCarrierTracking(guia: string): Promise<DailyInboxProposal> {
    const response = await fetch(`${API_BASE}/operations/carrier-lookup/${encodeURIComponent(guia)}/queue`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Error al preparar la operación con esa guía.');
    return data.proposal;
  },

  // `estado: 'accepted'` brings already-accepted rows that can still be reverted (16/08).
  async getUpsReconciliation(estado?: 'pending' | 'accepted'): Promise<UpsCostReconciliationRow[]> {
    const qs = estado === 'accepted' ? '?estado=aceptadas' : '';
    const response = await fetch(`${API_BASE}/ups-reconciliation${qs}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener la conciliación de costos UPS.");
    return await response.json();
  },

  // Undoes an accepted discrepancy: restores the shipment/tracking guide's previous costoFlete
  // and returns the row to the pending queue so it can be reviewed again.
  async revertUpsReconciliation(id: string): Promise<{ costoRestaurado: number }> {
    const response = await fetch(`${API_BASE}/ups-reconciliation/${id}/revert`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || "Error al revertir la aceptación del costo.");
    return data;
  },

  async acceptUpsReconciliation(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-reconciliation/${id}/accept`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al aceptar el costo real.");
    }
  },

  async dismissUpsReconciliation(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-reconciliation/${id}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al descartar la fila de conciliación.");
    }
  },

  // --- Uncollected shipment alerts (Tarea 3, prioridad baja) ---
  async pollUpsCollectionAlertsEmail(): Promise<{ skipped?: boolean; reason?: string; emailsSeen?: number; alertsProcessed?: number; errors?: { context: string; error: string }[] }> {
    const response = await fetch(`${API_BASE}/ups-collection-alerts/poll-email`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al revisar el correo de alertas de recolección.");
    }
    return await response.json();
  },

  async getUpsCollectionAlerts(): Promise<UpsCollectionAlertRow[]> {
    const response = await fetch(`${API_BASE}/ups-collection-alerts`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las alertas de recolección.");
    return await response.json();
  },

  async linkUpsCollectionAlert(id: string, recordId: string, motivoNoRecogida?: string, motivoNoRecogidaDetalle?: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-collection-alerts/${id}/link`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recordId, motivoNoRecogida, motivoNoRecogidaDetalle })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al vincular la alerta a la operación.");
    }
  },

  async dismissUpsCollectionAlert(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-collection-alerts/${id}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al descartar la alerta.");
    }
  },

  // --- Collection accounts (QR/bank account) for manual payment without a gateway (30/07) ---
  async getCuentasRecaudo(): Promise<CuentaRecaudo[]> {
    const response = await fetch(`${API_BASE}/collection-accounts`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las cuentas de recaudo.");
    return await response.json();
  },

  async createCuentaRecaudo(data: {
    banco: string; tipo: 'qr' | 'cuenta'; titular?: string; numeroCuenta?: string;
    tipoCuenta?: string; topeMonto?: number | null; activa?: boolean; orden?: number; qrFile?: File;
  }): Promise<void> {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'qrFile' || value === undefined || value === null) return;
      formData.append(key, String(value));
    });
    if (data.qrFile) formData.append('qr', data.qrFile);

    const response = await postFormWithProgress(`${API_BASE}/collection-accounts`, formData, {
      label: i18n.t('jobs.kinds.upload'),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al crear la cuenta de recaudo.");
    }
  },

  async updateCuentaRecaudo(id: string, data: {
    banco: string; tipo: 'qr' | 'cuenta'; titular?: string; numeroCuenta?: string;
    tipoCuenta?: string; topeMonto?: number | null; activa?: boolean; orden?: number; qrFile?: File;
  }): Promise<void> {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'qrFile' || value === undefined || value === null) return;
      formData.append(key, String(value));
    });
    if (data.qrFile) formData.append('qr', data.qrFile);

    const response = await postFormWithProgress(`${API_BASE}/collection-accounts/${id}`, formData, {
      method: 'PUT',
      label: i18n.t('jobs.kinds.upload'),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al actualizar la cuenta de recaudo.");
    }
  },

  async deleteCuentaRecaudo(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/collection-accounts/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al eliminar la cuenta de recaudo.");
    }
  },

  // --- Comprobantes crudos del Collection mailbox (30/07, infraestructura de the piece 2) ---
  async pollRecaudoComprobantesEmail(): Promise<{ skipped?: boolean; reason?: string; emailsSeen?: number; errors?: { context: string; error: string }[] }> {
    const response = await fetch(`${API_BASE}/collection-receipts/poll-email`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al revisar el correo del buzón de recaudo.");
    }
    return await response.json();
  },

  async getRecaudoComprobantes(): Promise<ComprobantePagoRecaudo[]> {
    const response = await fetch(`${API_BASE}/collection-receipts`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener los comprobantes de recaudo.");
    return await response.json();
  },

  async marcarRevisadoRecaudoComprobante(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/collection-receipts/${id}/mark-reviewed`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al marcar el comprobante como revisado.");
    }
  },

  /** W-9a: interruptor del «pago detectado desde el banco» (apagado por defecto). */
  async getPagoDetectado(): Promise<boolean> {
    const response = await fetch(`${API_BASE}/pago-detectado`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('No se pudo leer el interruptor de pago detectado.');
    return !!(await response.json()).activo;
  },
  async setPagoDetectado(activo: boolean): Promise<boolean> {
    const response = await fetch(`${API_BASE}/pago-detectado`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo }) });
    if (!response.ok) throw new Error('No se pudo cambiar el interruptor de pago detectado.');
    return !!(await response.json()).activo;
  },
  async getComprobantesCliente(): Promise<ComprobanteCliente[]> {
    const response = await fetch(`${API_BASE}/client-receipts`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener los comprobantes de clientes.");
    return await response.json();
  },

  async confirmarComprobanteCliente(id: string, preferredRecordIds?: string[], partialReason?: string): Promise<{ success: boolean; allocations?: { record_id: string; monto_asignado: number }[] }> {
    const response = await fetch(`${API_BASE}/client-receipts/${id}/confirm`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(preferredRecordIds?.length ? { preferredRecordIds } : {}),
        ...(partialReason ? { partialReason } : {}),
      }),
    });
    if (!response.ok) throw await paymentError(response, "Error al confirmar el pago.");
    return await response.json().catch(() => ({ success: true }));
  },

  /** T-14d: life line of an operation (same row access as the operation itself). */
  async getOperationTimeline(recordId: string): Promise<import('../src/lib/operationTimeline').TimelineEvent[]> {
    const response = await fetch(`${API_BASE}/records/${encodeURIComponent(recordId)}/timeline`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error("Error al obtener la línea de vida de la operación.");
    return (await response.json()).events;
  },

  /** T-14e: approved quotes still waiting for their payment (Accounting works them from Contabilidad). */
  async getQuotesAwaitingPayment(): Promise<{ id: string; consecutivo: string; fecha: string; cliente: string; pago: import('../src/lib/quotePayment').QuotePaymentSummary }[]> {
    const response = await fetch(`${API_BASE}/quotes/awaiting-payment`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error("Error al obtener las cotizaciones por cobrar.");
    return (await response.json()).items;
  },

  /** T-14e: Accounting registers a payment received outside the public link (support file required). */
  async registrarPagoCotizacion(quoteId: string, form: FormData): Promise<{ success: boolean; receiptId: string }> {
    const response = await fetch(`${API_BASE}/quotes/${quoteId}/register-payment`, {
      method: 'POST',
      // No Content-Type: the browser sets the multipart boundary.
      headers: { 'X-CSRF-Token': getCsrfToken() },
      body: form,
    });
    if (!response.ok) throw await paymentError(response, "Error al registrar el pago.");
    return await response.json();
  },

  async getComprobanteAllocationPreview(id: string): Promise<{
    amount: number;
    intendedRecordIds: string[];
    quoteOnly: boolean;
    items: { id: string; guia: string; fecha: string; totalAFacturar: number; yaAsignado: number; saldoPendiente: number; intended: boolean }[];
    suggested: { record_id: string; monto_asignado: number }[];
  }> {
    const response = await fetch(`${API_BASE}/client-receipts/${id}/allocation-preview`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error("Error al obtener la vista previa de asignación.");
    return await response.json();
  },

  async rechazarComprobanteCliente(id: string, motivo?: string): Promise<void> {
    const response = await fetch(`${API_BASE}/client-receipts/${id}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivo })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al rechazar el comprobante.");
    }
  },

  // --- Credit balance (task #19) ---
  async getSaldosFavor(): Promise<{ cliente_id: string; nombre: string; empresa: string; saldo: number }[]> {
    const response = await fetch(`${API_BASE}/credit-balance`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener saldos a favor.");
    return await response.json();
  },

  async getSaldoFavorCliente(clienteId: string): Promise<{ saldo: number; movimientos: any[] }> {
    const response = await fetch(`${API_BASE}/clients/${clienteId}/credit-balance`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener el saldo a favor del cliente.");
    return await response.json();
  },

  async registrarSaldoFavor(clienteId: string, monto: number, motivo: string): Promise<{ saldo: number }> {
    const response = await fetch(`${API_BASE}/credit-balance`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ clienteId, monto, motivo })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al registrar el saldo a favor.");
    }
    return await response.json();
  },

  // --- Weight adjustments (Epic C, 10/08) ---
  async getAjustesPeso(estado?: string): Promise<any[]> {
    const qs = estado && estado !== 'TODOS' ? `?estado=${encodeURIComponent(estado)}` : '';
    const response = await fetch(`${API_BASE}/weight-adjustments${qs}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener los ajustes de peso.");
    return await response.json();
  },

  // --- Commission collection account (Epic D, 10/08) ---
  async generarCuentaCobroComision(recordId: string): Promise<{ cuentaCobro: any }> {
    const response = await fetch(`${API_BASE}/commissions/${recordId}/generate-settlement-claim`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al generar la cuenta de cobro.");
    }
    return await response.json();
  },

  // Undoes commission settlement (16/08): deletes the collection account if present and
  // returns the commission to pending. Previously, the only way to fix a commission
  // settled by mistake was to void the entire shipment/tracking guide.
  async deshacerCuentaCobroComision(recordId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/commissions/${encodeURIComponent(recordId)}/settlement-claim`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Error al deshacer la liquidación de la comisión.");
    }
  },

  async marcarAjustePesoPagado(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/weight-adjustments/${id}/mark-paid`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al marcar el ajuste como pagado.");
    }
  },

  // --- Client portal pending balance: breakdown by shipment + contact sales rep ---
  async getPortalBalanceDetalle(): Promise<{
    items: { id: string; guia: string; fecha: string; totalAFacturar: number; saldoPendiente: number; comercial: string | null }[];
    comercial: { nombre: string; telefono: string } | null;
  }> {
    const response = await fetch(`${API_BASE}/portal/balance/detalle`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener el detalle del saldo pendiente.");
    return await response.json();
  },

  async uploadPortalReceipt(
    file: File,
    montoDeclarado: number,
    referencia?: string,
    opts?: { intendedRecordIds?: string[]; intendedQuoteId?: string },
  ): Promise<{ success: boolean; id: string }> {
    const formData = new FormData();
    formData.append('comprobante', file);
    formData.append('montoDeclarado', String(montoDeclarado));
    if (referencia) formData.append('referencia', referencia);
    if (opts?.intendedRecordIds?.length) {
      formData.append('intendedRecordIds', JSON.stringify(opts.intendedRecordIds));
    }
    if (opts?.intendedQuoteId) formData.append('intendedQuoteId', opts.intendedQuoteId);
    const response = await postFormWithProgress(`${API_BASE}/portal/balance/upload-receipt`, formData, {
      label: i18n.t('jobs.kinds.upload'),
    });
    const errorData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(errorData.error || "Error al subir el comprobante.");
    }
    return errorData;
  },

  async usarSaldoFavor(clienteId: string, monto: number, totalFactura: number, quoteId?: string): Promise<{ montoAplicado: number; saldoRestante: number }> {
    const response = await fetch(`${API_BASE}/credit-balance/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ clienteId, monto, totalFactura, quoteId })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al aplicar el saldo a favor.");
    }
    return await response.json();
  },

  async getCommercialGoals(): Promise<CommercialGoal[]> {
    const response = await fetch(`${API_BASE}/commercial-goals`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener metas comerciales.");
    return await response.json();
  },

  async saveCommercialGoal(goal: Partial<CommercialGoal>): Promise<void> {
    const response = await fetch(`${API_BASE}/commercial-goals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...goal,
        id: goal.id || getSafeUUID()
      })
    });
    if (!response.ok) throw new Error("Error al guardar meta comercial.");
  },

  async deleteCommercialGoal(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/commercial-goals/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al eliminar meta comercial.");
  },

  async getNotifications(params: { page: number; pageSize: number; search?: string; leida?: boolean; tipo?: string; prioridad?: string; archivada?: boolean; desde?: string; hasta?: string } & ServerSortParams): Promise<{ items: NotificationGroup[]; total: number; page: number; pageSize: number; totalPages: number; truncated: boolean }> {
    const qs = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) });
    if (params.search) qs.set('search', params.search);
    if (params.leida !== undefined) qs.set('leida', String(params.leida));
    if (params.tipo) qs.set('tipo', params.tipo);
    if (params.prioridad) qs.set('prioridad', params.prioridad);
    if (params.archivada !== undefined) qs.set('archivada', String(params.archivada));
    if (params.desde) qs.set('desde', params.desde);
    if (params.hasta) qs.set('hasta', params.hasta);
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/notifications?${qs.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener notificaciones.");
    return await response.json();
  },

  async getUnreadNotificationCount(): Promise<number> {
    const response = await fetch(`${API_BASE}/notifications/unread-count`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener el conteo de notificaciones.");
    const data = await response.json();
    return data.count || 0;
  },

  async markNotificationRead(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/notifications/${id}/read`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al marcar la notificación como leída.");
  },

  async markAllNotificationsRead(): Promise<void> {
    const response = await fetch(`${API_BASE}/notifications/read-all`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al marcar las notificaciones como leídas.");
  },

  async archiveNotification(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/notifications/${id}/archive`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al archivar la notificación.");
  },

  async unarchiveNotification(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/notifications/${id}/unarchive`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al desarchivar la notificación.");
  },

  async getNotificationPreferences(): Promise<{ soundEnabled: boolean; pushEnabled: boolean; perType: Record<string, { in_app: boolean; push: boolean }> }> {
    const response = await fetch(`${API_BASE}/notification-preferences`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener las preferencias de notificaciones.");
    return await response.json();
  },

  async updateNotificationPreferences(prefs: { soundEnabled?: boolean; pushEnabled?: boolean; typeOverrides?: { tipo: string; canal: 'in_app' | 'push'; habilitado: boolean }[] }): Promise<void> {
    const response = await fetch(`${API_BASE}/notification-preferences`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(prefs)
    });
    if (!response.ok) throw new Error("Error al actualizar las preferencias de notificaciones.");
  },

  async getTourProgress(): Promise<{ items: TourProgressItem[] }> {
    const response = await fetch(`${API_BASE}/tour/progress`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener el progreso de la guía interactiva.");
    return await response.json();
  },

  async saveTourProgress(data: { roleKey: string; stepIndex: number; status: 'in_progress' | 'completed' | 'dismissed' }): Promise<{ success: boolean; stepIndex: number }> {
    const response = await fetch(`${API_BASE}/tour/progress`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error("Error al guardar el progreso de la guía interactiva.");
    return await response.json();
  },

  async logTourEvent(data: { roleKey: string; stepId?: string; evento: 'started' | 'step_viewed' | 'completed' | 'abandoned' | 'dismissed' }): Promise<void> {
    const response = await fetch(`${API_BASE}/tour/events`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error("Error al registrar el evento de la guía interactiva.");
  },

  async getVapidPublicKey(): Promise<{ publicKey: string }> {
    const response = await fetch(`${API_BASE}/push/vapid-public-key`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener la llave pública de push.");
    return await response.json();
  },

  async subscribePush(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<void> {
    const response = await fetch(`${API_BASE}/push/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(subscription)
    });
    if (!response.ok) throw new Error("Error al registrar la suscripción push.");
  },

  async unsubscribePush(endpoint: string): Promise<void> {
    const response = await fetch(`${API_BASE}/push/subscribe`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ endpoint })
    });
    if (!response.ok) throw new Error("Error al cancelar la suscripción push.");
  },

  // --- Live Mailbox (08/08) --- mailboxKey: 'operations' | 'collections' | 'accounting'.
  // Aggregated count for the side-menu badge (15/08) -- 0 for mailboxes the user cannot
  // access or that are not configured; see GET /api/mailbox/unread-count.
  async getMailboxUnreadCount(): Promise<MailboxUnreadCounts> {
    const response = await fetch(`${API_BASE}/mailbox/unread-count`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error("Error al obtener el conteo de correos no leídos.");
    return await response.json();
  },

  async getMailboxMessages(mailboxKey: string, page: number, pageSize: number, unseenOnly?: boolean, search?: string): Promise<MailboxListResponse> {
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (unseenOnly) qs.set('unseenOnly', 'true');
    // Search is resolved by the IMAP server over the WHOLE mailbox, not the browser
    // over the visible page: with 102 pages, client-side filtering would be a search
    // that almost never finds anything.
    if (search && search.trim()) qs.set('search', search.trim());
    const response = await fetch(`${API_BASE}/mailbox/${mailboxKey}/messages?${qs.toString()}`, { headers: getAuthHeaders() });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al obtener los correos del buzón.");
    }
    return await response.json();
  },

  async getMailboxMessage(mailboxKey: string, uid: number): Promise<MailboxMessageDetail> {
    const response = await fetch(`${API_BASE}/mailbox/${mailboxKey}/messages/${uid}`, { headers: getAuthHeaders() });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al leer el correo.");
    }
    return await response.json();
  },

  getMailboxAttachmentUrl(mailboxKey: string, uid: number, partId: string): string {
    return `${API_BASE}/mailbox/${mailboxKey}/messages/${uid}/attachments/${encodeURIComponent(partId)}`;
  },

  async setMailboxMessageSeen(mailboxKey: string, uid: number, seen: boolean): Promise<void> {
    const response = await fetch(`${API_BASE}/mailbox/${mailboxKey}/messages/${uid}/seen`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ seen })
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al actualizar el correo.");
    }
  },

  async archiveMailboxMessage(mailboxKey: string, uid: number): Promise<void> {
    const response = await fetch(`${API_BASE}/mailbox/${mailboxKey}/messages/${uid}/archive`, { method: 'POST', headers: getAuthHeaders() });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al archivar el correo.");
    }
  },

  async deleteMailboxMessage(mailboxKey: string, uid: number): Promise<void> {
    const response = await fetch(`${API_BASE}/mailbox/${mailboxKey}/messages/${uid}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al eliminar el correo.");
    }
  },

  async reprocessMailboxMessage(uid: number, kind: 'invoice' | 'tracking'): Promise<{ success: true; recordId: string }> {
    const response = await fetch(`${API_BASE}/mailbox/operations/messages/${uid}/reprocess`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ kind })
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al reprocesar el correo.");
    }
    return await response.json();
  },

  async linkMailboxMessage(uid: number, params: { kind: 'invoice' | 'tracking'; recordId: string; trackingStatus?: string }): Promise<{ success: true; recordId: string }> {
    const response = await fetch(`${API_BASE}/mailbox/operations/messages/${uid}/link`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(params)
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al vincular el correo.");
    }
    return await response.json();
  },

  async pollUpsTrackingEmail(): Promise<{ skipped?: boolean; reason?: string; emailsSeen?: number; matched?: number; unmatched?: number; errors?: { context: string; error: string }[] }> {
    const response = await fetch(`${API_BASE}/ups-tracking/poll-email`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Error al revisar el correo de tracking UPS.");
    }
    return await response.json();
  },

  async getStagedUpsTrackingEvents(): Promise<StagedUpsTrackingEvent[]> {
    const response = await fetch(`${API_BASE}/ups-tracking-events/staged`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error("Error al obtener los eventos de tracking UPS en revisión.");
    return await response.json();
  },

  async linkStagedUpsTrackingEvent(stagedId: string, recordId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-tracking-events/staged/${stagedId}/link`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ recordId })
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al vincular el evento de tracking.");
    }
  },

  async discardStagedUpsTrackingEvent(stagedId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/ups-tracking-events/staged/${stagedId}`, {
      method: 'DELETE', headers: getAuthHeaders()
    });
    if (!response.ok) {
      let errorData: any = {};
      try { errorData = await response.json(); } catch (e) {}
      throw new Error(errorData.error || "Error al descartar el evento de tracking.");
    }
  },

  // --- On-call pickups UPS/FedEx (11/08, ver src/lib/carrierPickup.ts) ---

  async getPickups(params: {
    recordId?: string; estado?: string; operador?: string; search?: string;
    fechaDesde?: string; fechaHasta?: string; page?: number; pageSize?: number;
  } & ServerSortParams = {}): Promise<RecoleccionesPage> {
    const qs = new URLSearchParams();
    if (params.recordId) qs.set('recordId', params.recordId);
    if (params.estado && params.estado !== 'TODOS') qs.set('estado', params.estado);
    if (params.operador && params.operador !== 'TODOS') qs.set('operator', params.operador);
    if (params.search) qs.set('search', params.search);
    if (params.fechaDesde) qs.set('dateDesde', params.fechaDesde);
    if (params.fechaHasta) qs.set('dateHasta', params.fechaHasta);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/pickups?${qs.toString()}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al obtener las recolecciones.'));
    return await response.json();
  },

  /** Counts by status + today + overdue, in a single request (see GET /api/pickups/summary). */
  async getPickupsSummary(): Promise<ResumenRecolecciones> {
    const response = await fetch(`${API_BASE}/pickups/summary`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al obtener el resumen de recolecciones.'));
    return await response.json();
  },

  /** Status of the historical pickup sweep (21/08) -- reconstructed emails, not what
   *  was scheduled by this system (see src/lib/pickupHistoricoPoll.ts). */
  async getPickupHistoricalSweepStatus(): Promise<any> {
    const response = await fetch(`${API_BASE}/pickups/barrido-historico`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al obtener el estado del barrido.'));
    return await response.json();
  },

  async startPickupHistoricalSweep(): Promise<{ iniciado: boolean }> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups/barrido-historico`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo iniciar el barrido.');
      return data;
    });
  },

  async getPickupEmailHistory(params: { page?: number; pageSize?: number; operador?: string; soloAtados?: boolean; soloSinAtar?: boolean } & ServerSortParams = {}): Promise<any> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.operador) qs.set('operator', params.operador);
    if (params.soloAtados) qs.set('soloAtados', 'true');
    if (params.soloSinAtar) qs.set('soloSinAtar', 'true');
    appendSortParams(qs, params);
    const response = await fetch(`${API_BASE}/pickups/historico-correo?${qs.toString()}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al obtener el historial de recolecciones.'));
    return await response.json();
  },

  async createPickup(payload: Record<string, unknown>): Promise<Recoleccion> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo programar la recolección.'));
      return (await response.json()).recoleccion;
    });
  },

  async cancelPickup(id: string, motivo?: string): Promise<Recoleccion> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups/${id}/cancel`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ motivo })
      });
      if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo cancelar la recolección.'));
      return (await response.json()).recoleccion;
    });
  },

  async refreshPickups(): Promise<{ actualizadas: number; consultadas: number; aviso?: string }> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups/refresh`, { method: 'POST', headers: getAuthHeaders() });
      if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo consultar el estado en el operador.'));
      return await response.json();
    });
  },

  async sendPickupSupport(id: string, destinatario?: string): Promise<void> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups/${id}/enviar-soporte`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ destinatario })
      });
      if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo enviar el soporte de la recolección.'));
    });
  },

  /** Full pickup detail (info + status history), without querying the operator --
   * what is already stored in the system. */
  async getPickupDetail(id: string): Promise<RecoleccionDetalle> {
    const response = await fetch(`${API_BASE}/pickups/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo cargar el detalle de la recolección.'));
    return await response.json();
  },

  /** Queries the status of THAT pickup directly at the operator (point equivalent of
   * Tracking's "Consult"). Returns the same shape as `getPickupDetail`. */
  async queryPickupStatus(id: string): Promise<RecoleccionDetalle> {
    return runMeasuredJob({ kind: 'other', label: i18n.t('jobs.kinds.other'), stayRequired: true, requiresOnline: true }, async () => {
      const response = await fetch(`${API_BASE}/pickups/${id}/status`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(await errorTexto(response, 'No se pudo consultar el estado de la recolección en el operador.'));
      return await response.json();
    });
  },


  async getDocumentosAduanerosByRecord(recordId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE}/records/${recordId}/customs-documents`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al cargar documentos aduaneros.'));
    return await response.json();
  },

  async uploadDocumentoAduanero(recordId: string, formData: FormData): Promise<any> {
    const token = localStorage.getItem('token');
    const response = await postFormWithProgress(`${API_BASE}/records/${recordId}/customs-documents`, formData, {
      label: i18n.t('jobs.kinds.upload'),
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Error al subir el documento aduanero.');
    }
    return await response.json();
  },

  async deleteDocumentoAduanero(recordId: string, docId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/records/${recordId}/customs-documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await errorTexto(response, 'Error al eliminar el documento aduanero.'));
  },

  async downloadDocumentoAduanero(docId: string, filename: string): Promise<void> {
    await downloadWithProgress(`${API_BASE}/customs-documents/${docId}/download`, filename, i18n.t('jobs.kinds.download'));
  },

  // --- Partners API admin (design spec §2.4) ---
  async getPartners(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/partners`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener los partners.');
    return await response.json();
  },
  async getPartner(id: string): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener el partner.');
    return await response.json();
  },
  async createPartner(body: { name: string; email?: string; carrierPreferred?: string; defaultSalesRepId?: string | null; enabledEndpoints?: string[] | null }): Promise<any> {
    const response = await fetch(`${API_BASE}/partners`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al crear el partner.');
    return await response.json();
  },
  async updatePartner(id: string, body: Record<string, unknown>): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al actualizar el partner.');
    return await response.json();
  },
  async rotatePartnerCredentials(id: string, mode: 'test' | 'live'): Promise<{ mode: 'test' | 'live'; apiKey: string; apiSecret: string }> {
    const response = await fetch(`${API_BASE}/partners/${id}/rotate-credentials`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ mode }) });
    if (!response.ok) throw new Error('Error al rotar las credenciales.');
    return await response.json();
  },
  async setPartnerWebhook(id: string, url: string): Promise<{ webhookUrl: string | null; webhookSecret: string | null }> {
    const response = await fetch(`${API_BASE}/partners/${id}/webhook`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ url }) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al guardar el webhook.');
    return await response.json();
  },
  async getPartnerTariffs(partnerId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/tariffs`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener las tarifas.');
    return await response.json();
  },
  async createPartnerTariff(partnerId: string, body: { brackets: Array<{ minKg: number; maxKg: number; usdPerKg: number }>; allowedOrigins: string[]; validFrom?: string; validUntil?: string }): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/tariffs`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al crear la tarifa.');
    return await response.json();
  },
  async setPartnerRebateTiers(partnerId: string, tiers: Array<{ minKg: number; maxKg: number; pct: number }>): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/rebate-tiers`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ tiers }) });
    if (!response.ok) throw new Error('Error al guardar los tramos de incentivo.');
    return await response.json();
  },
  async setPartnerSurcharges(partnerId: string, surcharges: Array<{ type: string; amount: number; isPercent: boolean; active?: boolean }>): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/surcharges`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ surcharges }) });
    if (!response.ok) throw new Error('Error al guardar los recargos.');
    return await response.json();
  },
  async runPartnerLiquidation(partnerId: string, year: number, month: number): Promise<any> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/liquidations/run`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ year, month }) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al liquidar el partner.');
    return await response.json();
  },
  async getPartnerLiquidations(partnerId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE}/partners/${partnerId}/liquidations`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener las liquidaciones.');
    return await response.json();
  },
  async getRemoteZones(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/remote-zones`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener las zonas remotas.');
    return await response.json();
  },
  async createRemoteZone(originCountry: string, location: string): Promise<any> {
    const response = await fetch(`${API_BASE}/remote-zones`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ originCountry, location }) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al crear la zona remota.');
    return await response.json();
  },
  async deleteRemoteZone(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/remote-zones/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al eliminar la zona remota.');
  },
  async getOversizeConfig(): Promise<{ maxDimensionCm: number | null; maxWeightPerPieceKg: number | null; maxVolumeM3: number | null }> {
    const response = await fetch(`${API_BASE}/oversize-config`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener la configuración de sobredimensión.');
    return await response.json();
  },
  async setOversizeConfig(body: { maxDimensionCm?: number | null; maxWeightPerPieceKg?: number | null; maxVolumeM3?: number | null }): Promise<any> {
    const response = await fetch(`${API_BASE}/oversize-config`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error('Error al guardar la configuración de sobredimensión.');
    return await response.json();
  },

  // --- Agency network: self-service for the reduced agency interface (design spec §3.2) ---
  async getMyAgency(): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies/me`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al obtener tu agencia.');
    return await response.json();
  },

  // --- Agente de IA de WhatsApp (W-2) ---
  async whatsappAgente<T = any>(path: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', body?: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}/whatsapp-agente/${path}`, { method, headers: getAuthHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'No se pudo completar la acción del agente de WhatsApp.');
    return await response.json();
  },

  // --- Agency network admin (design spec §3.5) ---
  async getAgencies(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/agencies`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener las agencias.');
    return await response.json();
  },
  async getAgency(id: string): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener la agencia.');
    return await response.json();
  },
  async createAgency(body: Record<string, unknown>): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al crear la agencia.');
    return await response.json();
  },
  async updateAgency(id: string, body: Record<string, unknown>): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al actualizar la agencia.');
    return await response.json();
  },
  async assignAgencyUser(agencyId: string, userId: string): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies/${agencyId}/users`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ userId }) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al asignar el usuario.');
    return await response.json();
  },
  async unassignAgencyUser(agencyId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/agencies/${agencyId}/users/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al remover el usuario.');
  },
  async runAgencyLiquidation(agencyId: string, year: number, month: number): Promise<any> {
    const response = await fetch(`${API_BASE}/agencies/${agencyId}/liquidations/run`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ year, month }) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Error al liquidar la agencia.');
    return await response.json();
  },
  async getAgencyLiquidations(agencyId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE}/agencies/${agencyId}/liquidations`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Error al obtener las liquidaciones.');
    return await response.json();
  },
};

/** @deprecated Use English method names (getClients, getSalesReps, getPickups, etc.). */
Object.assign(dataService, {
  getClientes: (...args: Parameters<typeof dataService.getClients>) => dataService.getClients(...args),
  getClienteById: (...args: Parameters<typeof dataService.getClientById>) => dataService.getClientById(...args),
  getClientesPaginated: (...args: Parameters<typeof dataService.getClientsPaginated>) => dataService.getClientsPaginated(...args),
  saveCliente: (...args: Parameters<typeof dataService.saveClient>) => dataService.saveClient(...args),
  deleteCliente: (...args: Parameters<typeof dataService.deleteClient>) => dataService.deleteClient(...args),
  getComerciales: (...args: Parameters<typeof dataService.getSalesReps>) => dataService.getSalesReps(...args),
  saveComercial: (...args: Parameters<typeof dataService.saveSalesRep>) => dataService.saveSalesRep(...args),
  deleteComercial: (...args: Parameters<typeof dataService.deleteSalesRep>) => dataService.deleteSalesRep(...args),
  getRecolecciones: (...args: Parameters<typeof dataService.getPickups>) => dataService.getPickups(...args),
  getResumenRecolecciones: (...args: Parameters<typeof dataService.getPickupsSummary>) => dataService.getPickupsSummary(...args),
  getEstadoBarridoRecolecciones: (...args: Parameters<typeof dataService.getPickupHistoricalSweepStatus>) => dataService.getPickupHistoricalSweepStatus(...args),
  iniciarBarridoRecolecciones: (...args: Parameters<typeof dataService.startPickupHistoricalSweep>) => dataService.startPickupHistoricalSweep(...args),
  getHistoricoRecolecciones: (...args: Parameters<typeof dataService.getPickupEmailHistory>) => dataService.getPickupEmailHistory(...args),
  crearRecoleccion: (...args: Parameters<typeof dataService.createPickup>) => dataService.createPickup(...args),
  cancelarRecoleccion: (...args: Parameters<typeof dataService.cancelPickup>) => dataService.cancelPickup(...args),
  refrescarRecolecciones: (...args: Parameters<typeof dataService.refreshPickups>) => dataService.refreshPickups(...args),
  enviarSoporteRecoleccion: (...args: Parameters<typeof dataService.sendPickupSupport>) => dataService.sendPickupSupport(...args),
  getRecoleccionDetalle: (...args: Parameters<typeof dataService.getPickupDetail>) => dataService.getPickupDetail(...args),
  consultarEstadoRecoleccion: (...args: Parameters<typeof dataService.queryPickupStatus>) => dataService.queryPickupStatus(...args),
});

/** Extracts the real backend message instead of swallowing it with a generic one -- pickup
 * errors (invalid time window, shipment not generated, reschedule cap) are actionable and
 * the operator needs to read them as-is. */
async function errorTexto(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
}
